import React, { createContext, useReducer, useEffect } from 'react'
import showdown from 'showdown'

import { FC_FLOW_PRE_URL, FC_FLOW_POS_URL, getPlans } from './services/api'
import { getContent, getTeamContent } from './services/contentful'
import CONFIG from './config'

const { PROMO_CODE } = CONFIG
const converter = new showdown.Converter()

export const initialState = {
  ready: false,
  loading: true,
  mainBonus: 0,
  regulamento: {
    validity: '',
    title: '',
    content: '',
  },
  isHUB: PROMO_CODE.indexOf('HUB') > -1,
  promoCode: PROMO_CODE,
  promoCodesAllowed: [],
  plans: { pre: null, pos: null },
  contentful: null,
}

const AppContext = createContext(initialState)
const { Provider } = AppContext

const ACTIONS = {
  SET_READY: 'setReady',
  SET_LOADING: 'setLoading',
  SET_PLANS: 'setPlans',
  SET_MAIN_BONUS: 'setMainBonus',
  SET_REGULAMENTO: 'setRegulamento',
  SET_CONTENTFUL: 'setContentful',
}

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_READY:
      return {
        ...state,
        ready: action.payload,
      }
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case ACTIONS.SET_PLANS:
      return {
        ...state,
        plans: action.payload,
      }

    case ACTIONS.SET_MAIN_BONUS:
      return {
        ...state,
        mainBonus: action.payload,
      }

    case ACTIONS.SET_REGULAMENTO:
      return {
        ...state,
        regulamento: action.payload,
      }

    case ACTIONS.SET_CONTENTFUL:
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}

const StateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  async function loadContentfull() {
    let res
    try {
      res = await getContent()
      const team = await getTeamContent()

      const promoCode = `HUB-${team.fields.promocode}`

      dispatch({
        type: ACTIONS.SET_CONTENTFUL,
        payload: {
          isHUB: promoCode.indexOf('HUB') > -1,
          promoCode,
          promoCodesAllowed: team.fields.promocodes_allowed,
          contentful: {
            ...res.fields,
            team,
            regulamento: converter.makeHtml(res.fields.regulamento),
            regulamentoSecundario: converter.makeHtml(res.fields.regulamentoSecundario),
          },
        },
      })
    } catch (error) {
      console.error(error)
    }
  }

  async function loadOffer(code) {
    dispatch({
      type: ACTIONS.SET_LOADING,
      payload: true,
    })

    const currentOffers = [state.contentful.promo, state.contentful.promo2]

    if (!currentOffers.includes(code.toUpperCase())) {
      dispatch({
        type: ACTIONS.SET_LOADING,
        payload: false,
      })
      return false
    }

    const plans = await getPlans(code)

    let promoCode = ''
    if (plans && plans.pre && plans.pre.codPromocional) promoCode = plans.pre.codPromocional

    let htmlRegulamento = ''
    if (state && state.contentful && state.contentful.regulamento) htmlRegulamento = state.contentful.regulamento

    if (state.isHUB) {
      if (
        promoCode === state.contentful.promo ||
        promoCode === `${state.contentful.promo}PRE` ||
        promoCode === `${state.contentful.promo}POS`
      )
        htmlRegulamento = state.contentful.regulamento
      else if (
        promoCode === state.contentful.promo2 ||
        promoCode === `${state.contentful.promo2}PRE` ||
        promoCode === `${state.contentful.promo2}POS`
      )
        htmlRegulamento = state.contentful.regulamentoSecundario
    }

    dispatch({
      type: ACTIONS.SET_REGULAMENTO,
      payload: {
        title: '',
        validity: state.contentful.validade,
        content: htmlRegulamento,
      },
    })
    dispatch({
      type: ACTIONS.SET_PLANS,
      payload: plans,
    })
    dispatch({
      type: ACTIONS.SET_MAIN_BONUS,
      payload: plans && plans.pre ? plans.pre.numeroMesesBonificacao : CONFIG.DEFAULT_OFFER,
    })
    dispatch({
      type: ACTIONS.SET_LOADING,
      payload: false,
    })

    if (plans && plans.pre) {
      dispatch({
        type: ACTIONS.SET_READY,
        payload: true,
      })
    }

    return true
  }

  function openPromotion(type) {
    return function () {
      let url = FC_FLOW_PRE_URL
      let code = state.plans[type.toLowerCase()].codPromocional
      if (type === 'POS') url = FC_FLOW_POS_URL
      window.location.href = `${url}?origem=lp&codigo=${code}`
    }
  }

  useEffect(() => {
    if (state.contentful) {
      const promoCode = state.isHUB ? state.promoCode.split('-')[1] : state.promoCode
      loadOffer(promoCode).then(() => {
        dispatch({
          type: ACTIONS.SET_LOADING,
          payload: false,
        })

        dispatch({
          type: ACTIONS.SET_READY,
          payload: !state.isHUB,
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.contentful])

  useEffect(() => {
    loadContentfull()
  }, [])

  return (
    <Provider
      value={{
        state,
        dispatch,
        openPromotion,
        loadOffer,
      }}>
      {children}
    </Provider>
  )
}

export { AppContext, StateProvider, ACTIONS }
